import { featureCollection, point, type Position } from '@turf/helpers';
import type { MapMouseEvent, SymbolLayerSpecification } from 'mapbox-gl';
import { type MutableRefObject, useContext, useEffect, useMemo, useState } from 'react';
import { Layer, Source } from 'react-map-gl/mapbox';
import useValueRef from '@alltrails/core/hooks/useValueRef';
import useMap from '../../../hooks/useMap';
import { useAppSelector } from '../../../redux';
import { selectCustomRouteActiveSegment } from '../../../redux/customRoute';
import type { CustomRouteSegment } from '../../../types/CustomRoute';
import {
  customRouteActiveSegmentChevronsId,
  customRouteActiveSegmentPolylineId,
  customRouteAddHandlePreviewId,
  defaultSymbolLayerLayoutProps,
  getLayerBeforeId,
  osmLinesId
} from '../../../utils/layers';
import handleSvg from '../../../utils/svgDefinitions/handleSvg';
import { MapLayerInteractionsContext } from '../../MapLayerInteractionsProvider';
import { getAddHandlePreviewPoint } from './useAddHandle';

const SOURCE_ID = 'custom-route-add-handle-preview-source';

// Layers whose clicks add a handle to the active segment polyline (see onActivePolylineClick and onOsmLineClick in DrawControlsProvider)
const addHandleLayerIds = [customRouteActiveSegmentPolylineId, customRouteActiveSegmentChevronsId, osmLinesId];

const layout: SymbolLayerSpecification['layout'] = {
  ...defaultSymbolLayerLayoutProps,
  'icon-image': handleSvg.name,
  'icon-allow-overlap': true
};

const paint: SymbolLayerSpecification['paint'] = {
  'icon-opacity': 0.8
};

type AddHandlePreviewProps = {
  getIsActionPending: () => boolean;
  isMenuOpenRef: MutableRefObject<boolean>;
};

/**
 * A see-through handle where a click on the active segment polyline would add one.
 * Ordered with the feature layers but takes no events, so hovering it doesn't leave the polyline and clicks still reach it.
 * Mounted only while drawing is enabled.
 */
const AddHandlePreview = ({ getIsActionPending, isMenuOpenRef }: AddHandlePreviewProps) => {
  const map = useMap();
  const beforeId = getLayerBeforeId(map, customRouteAddHandlePreviewId);
  const { registerEventHandler, unregisterEventHandler } = useContext(MapLayerInteractionsContext);
  const activeSegment = useAppSelector(selectCustomRouteActiveSegment);
  const activeSegmentRef = useValueRef(activeSegment);
  const [hovered, setHovered] = useState<{ point: Position; segment: CustomRouteSegment } | undefined>(undefined);

  useEffect(() => {
    // Also handles mouseleave, which comes with the mousemove event when a higher layer is now on top and without features when the mouse leaves every layer
    const updateHovered = (event: MapMouseEvent) => {
      const topLayerId = event.features?.[0]?.layer?.id;
      const wouldClickAddHandle = !!topLayerId && addHandleLayerIds.includes(topLayerId) && !getIsActionPending() && !isMenuOpenRef.current;
      const segment = activeSegmentRef.current;
      const hoveredPoint = wouldClickAddHandle ? getAddHandlePreviewPoint(event.features, event.lngLat, segment) : undefined;
      setHovered(hoveredPoint && segment ? { point: hoveredPoint, segment } : undefined);
    };

    addHandleLayerIds.forEach(layerId => {
      registerEventHandler(layerId, 'mousemove', updateHovered);
      registerEventHandler(layerId, 'mouseleave', updateHovered);
    });

    return () => {
      addHandleLayerIds.forEach(layerId => {
        unregisterEventHandler(layerId, 'mousemove', updateHovered);
        unregisterEventHandler(layerId, 'mouseleave', updateHovered);
      });
    };
  }, [activeSegmentRef, getIsActionPending, isMenuOpenRef, registerEventHandler, unregisterEventHandler]);

  // The hovered position is stale once the segment changes, e.g. after the click adds the handle
  const position = hovered && hovered.segment === activeSegment ? hovered.point : undefined;

  // Hidden with the layer's visibility rather than by emptying the source: mapbox kept drawing the last symbol after
  // setData with an empty feature collection, so the source keeps the last position while the handle is hidden
  const [lastPosition, setLastPosition] = useState(position);
  if (position && position !== lastPosition) {
    setLastPosition(position);
  }

  const data = useMemo(() => featureCollection(lastPosition ? [point(lastPosition)] : []), [lastPosition]);
  const isVisible = !!position;
  const visibleLayout = useMemo<SymbolLayerSpecification['layout']>(() => ({ ...layout, visibility: isVisible ? 'visible' : 'none' }), [isVisible]);

  return (
    <Source id={SOURCE_ID} type="geojson" data={data}>
      <Layer id={customRouteAddHandlePreviewId} source={SOURCE_ID} type="symbol" layout={visibleLayout} paint={paint} beforeId={beforeId} />
    </Source>
  );
};

export default AddHandlePreview;
